import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';
import CoursesHelp from './CoursesHelp';
import CoursePanel from './CoursePanel';
import Paginator from './Paginator';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';

import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import useMediaQuery from '@mui/material/useMediaQuery';
import clsx from 'clsx';
import useStyles from '../styles/Styles'

import { loadSettings, loadCoursesInfo, loadCourses, loadMember, fetchCart, loadEnrolmentsSettings } from '../actions';

import _ from 'lodash';
import Pluralize from 'pluralize';
import { isTrue, maybeWithPrefix, fixedOptions, daysSorter } from '../utils';

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(isSameOrAfter);
dayjs.extend(updateLocale);

dayjs.updateLocale("en", { 
  weekStart: 1, // First day of week is Monday
  yearStart: 7  // First week of year must contain 1 January (7 + 1 - 1)
});


/*
If logged out and umas_status === 0:
	Display sorry message
*/

const Courses = () => {
	const dispatch = useDispatch();
	const classes = useStyles();
	
	const generalSettings = useSelector(state => state.settings.general);
	const courseLabels = useSelector(state => state.settings.labels_courses.data);
	const memberLabels = useSelector(state => state.settings.labels_members.data);
	const courses = useSelector(state => state.courses.data);
	const fetchingCourses = useSelector(state => state.courses.fetching);
	const fetchedCourses = useSelector(state => state.courses.expires);
	const errorMsg = useSelector(state => state.errorMessage);
	const coursesSettings = useSelector(state => state.settings.courses.data);
	const enrolmentsSettings = useSelector(state => state.settings.enrolments.data);
	const courseCategories = useSelector(state => state.settings.course_categories.data);
	const courseFrequencies = useSelector(state => state.settings.course_frequencies.data);
	const courseTypes = useSelector(state => state.settings.course_types.data);
	const courseTutors = useSelector(state => state.settings.tutors.data);
	const courseVenues = useSelector(state => state.settings.venues.data);
	const courseTerms = useSelector(state => state.settings.terms.data);
	const user = useSelector(state => state.user);
	const member = useSelector(state => state.member.data);
	const permissions = useSelector(state => state.permissions);
	
	const cart = useSelector(state => state.cart.data);
	// We don't need to dispatch fetchEnrolments because it's automatically dispatched after each loadCourses request
	const enrolments = useSelector(state => state.enrolments.data);
	
	const isSm = useMediaQuery('(min-width:650px)');
	const isSmd = useMediaQuery('(min-width:750px)');
	const isMd = useMediaQuery('(min-width:960px)');
	const isMdl	= useMediaQuery('(min-width:1060px)');

	const days = fixedOptions('days');
	
	const [unVaxxed, setUnvaxxed] = useState(true);
	useEffect(() => {
		if(isTrue(generalSettings.data.vaccine_rules_on) && !isTrue(member.vaccinated)){
			setUnvaxxed(true);
		} else {
			setUnvaxxed(false);
		}
	}, [generalSettings, member])

	const [enrolmentCounts, setEnrolmentCounts] = useState({
			category_counts: {},
			total: 0,
			counted_total: 0
	});
	useEffect(() => {
		// Waitlist and deleted ENROLMENTS aren't counted towards limits
		// Subscriptions aren't counted: cart may include subscription = 1 
		// Waitlist items in CART are counted, because we don't know for sure whether they will be a waitlist or not at the time of checkout, as this depends on other members' enrolments 
		// Only count within current membership date range
		
		const allEnrolments = _.chain({}).assign(enrolments.enrolled, _.keyBy(cart, 'course_code'))
			.pickBy(e => {
				return e.start_date <= enrolmentsSettings.member_courses_end_date &&
					e.finish_date >= enrolmentsSettings.member_courses_start_date 
					&& !isTrue(e.subscription) && e.status !== 'Deleted'
			}).mapValues('category_code').value();
			
		const counts = {
			total: _.size(allEnrolments),
			counted_total: _.chain(allEnrolments).pickBy(e => {
				return !_.has(courseCategories, e) || !isTrue(courseCategories[e].exempt_from_global_max_enrol)
			}).size().value(),
			category_counts: _.chain(allEnrolments).pickBy(e => {
				return _.has(courseCategories, e)
			}).countBy(e => e).value()
		}
		setEnrolmentCounts(counts);	
	}, [enrolments, cart, enrolmentsSettings, courseCategories]);
	
	const [fieldNames, setFieldNames] = useState({
		code: "Course code", 
		name: "Course name", 
		category_code: "Category", 
		frequency_code: "Frequency", 
		type_code: "Course type", 
		start_date: 'Start date', 
		end_date: 'Finish date', 
		start_time: 'Start time', 
		venue_code: "Venue", 
		day: 'Day of week', 
		is_new: "New courses", 
		tutor: "Tutor",
		tutor2: "",
		fee1: "Full fee", 
		detail: 'Detail', 
		additional_detail: 'Additional information', 
	});
	
	useEffect(() => {
		setFieldNames(f => { return {...f, 
			code: courseLabels.courseCapLabel + " code", 
			name: courseLabels.courseCapLabel + " name", 
			type_code: courseLabels.courseCapLabel + " type",
			is_new: "New " + courseLabels.courseLabel,
			category_code: courseLabels.category, 
			frequency_code: courseLabels.frequency, 
			venue_code: courseLabels.venue, 
			tutor: courseLabels.tutor, 
			tutor2: courseLabels.tutor2, 
			fee1: courseLabels.fee1
		}});

	}, [courseLabels]);

	useEffect(() => {
		if(user.member_no > 0){
			dispatch(loadMember());
			dispatch(fetchCart());
		}
	}, [dispatch, user])

	useEffect(() => {
		if(generalSettings.expires > 0){
			dispatch(loadSettings('courses'));
			dispatch(loadEnrolmentsSettings());
			dispatch(loadCoursesInfo('general'));
		}
	}, [dispatch, generalSettings]);

	const [timespanOptions, setTimespanOptions] = useState({
		"This year": [dayjs().startOf('year').format('YYYY-MM-DD'), dayjs().endOf('year').format('YYYY-MM-DD')],
		"Next year": [dayjs().startOf('year').add(1, 'y').format('YYYY-MM-DD'), dayjs().endOf('year').add(1, 'y').format('YYYY-MM-DD')],
		"This month": [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')],
		"Next month": [dayjs().startOf('month').add(1, 'M').format('YYYY-MM-DD'), dayjs().endOf('month').add(1, 'M').format('YYYY-MM-DD')],
		"This week": [dayjs().startOf('week').format('YYYY-MM-DD'), dayjs().endOf('week').format('YYYY-MM-DD')],
		"Next week": [dayjs().startOf('week').add(1, 'w').format('YYYY-MM-DD'), dayjs().endOf('week').add(1, 'w').format('YYYY-MM-DD')],
	});
	const [timespan, setTimespan] = useState(coursesSettings.default_list_timespan);
	
	useEffect(() => {
		// Terms that haven't finished or that are set as the default list timespan
		const reduced = _.reduce(courseTerms, (result, value, key) => {
			if(coursesSettings.default_list_timespan === key || 
				dayjs(value.end_date).isSameOrAfter(dayjs().startOf('day')) ){
					result[key] = [value.start_date, value.end_date];
				}
				return result;
		}, {});
		if(coursesSettings.member_courses_start_date !== ''){
			reduced["Current timetable"] = [coursesSettings.member_courses_start_date, coursesSettings.member_courses_end_date];
		}
		setTimespanOptions(t => {return {...reduced, ...t}})
	}, [courseTerms, coursesSettings])
	
	useEffect(() => {
		if(!isTrue(coursesSettings.hide_tutor_from_public) || user.member_no > 0){
			dispatch(loadCoursesInfo('tutors'));
		}
		
	}, [dispatch, coursesSettings, user]);
	
	useEffect(() => {
		if(_.has(timespanOptions, timespan)){
			dispatch(loadCourses(...timespanOptions[timespan]))
		}
	}, [dispatch, timespan, timespanOptions, user])
	
	const [tutorSorter, setTutorSorter] = useState([]);
	useEffect(() => {
		const extraNames = _.chain(courses).map('tutor').uniq().map(t => {
			return {code: t, tutor_name: t}
		}).value();
		
		const sorter = _.chain(courseTutors).map(t => { 
			return t
		}).unionBy(extraNames, 'code').sortBy('tutor_name').keyBy('code')
		.mapValues('tutor_name').value();
		
		setTutorSorter(sorter);
		
	}, [courseTutors, courses])
	
	const fields = {
		code: { 
			groupable: false, 
			sortable: !isTrue(coursesSettings.hide_code_in_list), 
			displayable: true,
		},
		name: { 
			groupable: false, 
			sortable: true, 
			displayable: true,
		},
		category_code: { 
			groupable: true, 
			sortable: true, 
			displayable: true,
		},
		frequency_code: { 
			groupable: !isTrue(coursesSettings.hide_frequency_in_list),
			sortable: !isTrue(coursesSettings.hide_frequency_in_list), 
			displayable: true,
		},
		type_code: { 
			groupable: true, 
			sortable: true, 
			displayable: true,
		},
		start_date: { 
			groupable: false, 
			sortable: true, 
			displayable: true,
		},
		end_date: { 
			groupable: false, 
			sortable: true, 
			displayable: true,
		},
		start_time: { 
			groupable: false, 
			sortable: true, 
			displayable: true,
		},
		venue_code: { 
			groupable: true, 
			sortable: true, 
			displayable: true,
		},
		day: { 
			groupable: true, 
			sortable: true, 
			displayable: true,
		},
		is_new: { 
			groupable: true, 
			sortable: true, 
			displayable: true,
		},
		tutor: { 
			groupable:  !isTrue(coursesSettings.hide_tutor_in_list) && (user.member_no > 0 || !isTrue(coursesSettings.hide_tutor_from_public)),
			sortable:  !isTrue(coursesSettings.hide_tutor_in_list) && (user.member_no > 0 || !isTrue(coursesSettings.hide_tutor_from_public)), 
			displayable: user.member_no > 0 || !isTrue(coursesSettings.hide_tutor_from_public)
		},
		tutor2: {  
			groupable: false, 
			sortable: false,
			displayable: courseLabels.tutor2 !== '' && (user.member_no > 0 || !isTrue(coursesSettings.hide_tutor_from_public))
		},
		fee1: { 
			groupable: false, 
			sortable: false,
			displayable: true
		},
		detail: { 
			groupable: false, 
			sortable: false,
			displayable: true
		},
		additional_detail: { 
			groupable: false, 
			sortable: false,
			displayable: coursesSettings.show_additional_detail
		},
	}
	
	const getGroupable = () => {
		return _.chain(fields)
			.pickBy({groupable: true})
			.mapValues((v, f) => { return fieldNames[f] })
			.value()
	}
	
	const getSortable = () => {
		return _.chain(fields)
			.pickBy({sortable: true})
			.mapValues((v, f) => { return fieldNames[f] })
			.value()		
	}
	
	const [groupOptions, setGroupOptions] = useState(getGroupable());
	const [sortOptions, setSortOptions] = useState(getSortable());
	// getGroupable() and getSortable() don't need to be dependencies
	useEffect(() => {
		setGroupOptions(getGroupable());
		setSortOptions(getSortable()); // eslint-disable-next-line
	}, [coursesSettings, fieldNames])
	
	const [group, setGroup] = useState('category_code');
	const [sort, setSort] = useState('day');
	
	const [exclusions, setExclusions] = useState({
		finished: true,
		full: false,
		closed: true
	})
	useEffect(() => {
		let e = _.cloneDeep(exclusions);
		if(isTrue(coursesSettings.hide_finished_in_list)){
			e = _.pick(e, ['full', 'closed']);
		}
		if(isTrue(coursesSettings.default_show_closed_courses)){
			e.closed = false;
		}
		setExclusions(e);
		
		if(coursesSettings.default_list_timespan){
			setTimespan(coursesSettings.default_list_timespan);
		}
		if(coursesSettings.default_list_sort_first){
			setGroup(coursesSettings.default_list_sort_first);
		}
		if(coursesSettings.default_list_sort_second){
			setSort(coursesSettings.default_list_sort_second);
		} // eslint-disable-next-line
	}, [coursesSettings])
	
	const [keyword, setKeyword] = useState('');
	
	const [showCategories, setShowCategories] = useState({});
	useEffect(() => {
		const cc = _.mapValues(courseCategories, (c) => {return true});
		if(!_.has(cc, 'other')){
			cc.other = true
		}
		setShowCategories(cc);
	},[courseCategories])

	const [filteredCourses, setFilteredCourses] = useState(courses);
	useEffect(() => {
		const filter = {
			is_subscription: 0
		}
		if(exclusions.closed){
			filter.is_closed = 0;
		}
		if(member.distance_member_type){
			filter.distance_course = 1;
		}
		
		const restrictDates = (c, by) => {
			if(!_.has(timespanOptions, by)){
				return true;
			}
			
			const dateLimits = timespanOptions[by];
			return _.some(_.split(c.dates, ','), (d) => {
				return d >= dateLimits[0] && d <= dateLimits[1];
			})
		}
		
		const keywordSearch = (c, keyword) => {
			
			if(_.isEmpty(keyword)){
				return true;
			} else {
				
				const searchFields = [
					'code',
					'name',
					'category_code',
					'detail',
					'additional_detail'
				];
				
				return _.some(searchFields, (s) => {
					return _.includes(c[s].toUpperCase(), keyword);
				}) || (_.has(courseCategories, c.category_code) && _.includes(courseCategories[c.category_code].description.toUpperCase(), keyword));
			} 
			
		}
		
		const doSort = (c, by) => {

			switch(by){
				case 'day':
					return daysSorter[c.day];
				case 'is_new':
					const newSorter = [1, 0];
					return newSorter[c.is_new];
				case 'tutor':
					return tutorSorter[c.tutor]
				default:
					return c[by];
			}
		}
		
		setFilteredCourses(_.chain(courses)
			.filter((c) => restrictDates(c, timespan))
			.filter(filter)
			.filter((c) => keywordSearch(c, keyword))
			.filter((c) => {
				return (showCategories[c.category_code] || (showCategories.other && !_.has(showCategories, c.category_code)))
			})
			.filter((c) => {
				return exclusions.full ? c.enrolled < c.max_no_student : true
			})
			.filter((c) => {
				return (exclusions.finished || isTrue(coursesSettings.hide_finished_in_list)) ? !isTrue(c.is_finished) : true
			})
			.sortBy((c) => [doSort(c, group), doSort(c, sort)])
			.value()
		); // eslint-disable-next-line
	},[courses, group, sort, exclusions, coursesSettings, keyword, showCategories, timespan, timespanOptions, member])
	
	const [page, setPage] = useState(0);
	const [pages, setPages] = useState(0);
	
	const perPage = 20;
	
	useEffect(() => {
		setPage(1);
		setPages(Math.ceil(filteredCourses.length / perPage));
	}, [filteredCourses])
	
	const [showingCourses, setShowingCourses] = useState([]);
	useEffect(() => {
		
		if(group === 'is_new'){
			setShowingCourses(
				_.chain(filteredCourses)
				.slice(perPage * (page-1), perPage*page)
				.groupBy((c) => {
					return c.is_new ? 'New' : 'Not new'
				})
				.value()
			);
		} else {
			setShowingCourses(
				_.chain(filteredCourses)
				.slice(perPage * (page-1), perPage*page)
				.groupBy(group)
				.value()
			);
		}
		
	}, [page, group, filteredCourses])
		
	const getDescription = (obj, key) => {
		
		if(_.isNil(key)){
			return '';
		}
		if(_.isString(obj)){
			switch(obj){
				case 'day':
					obj = days;
					break;
				case 'category_code':
					obj = courseCategories;
					break;
				case 'frequency_code':
					obj = courseFrequencies;
					break;
				case 'type_code':
					obj = courseTypes;
					break;
				case 'tutor':
				case 'tutor2':
					if(!fields[obj].displayable){ return ''; }
					return _.has(courseTutors, key) ? courseTutors[key].tutor_name : key.trim();
				case 'venue_code':
					return _.has(courseVenues, key) ? courseVenues[key].name : key.trim();
				case 'start_date':
				case 'end_date':
				case 'next_date':
					return dayjs(key).format('D MMM YYYY');
				case 'start_time':
				case 'end_time':
					return dayjs('2020-01-01 '+key).format('h:mm A');
				case 'is_new':
					return key;
				default:
					return key.trim();
			}
		}
		return _.has(obj, key) ? (_.isObject(obj[key]) ? obj[key].description : obj[key]) : key.trim();

	}
	
	const CourseGroupHeader = (props) => (
		<Paper className={clsx(classes.innerContent, classes.coursesGroup)} square={true} >
		<Typography variant="subtitle2">{groupOptions[group]}</Typography>
		<Typography variant="h6">{
			_.includes(['day', 'is_new', 'tutor'], group) ?
			getDescription(group, props.g) :
			maybeWithPrefix(props.g, getDescription(group, props.g))
		}</Typography>

		{isMd ?
		<Grid container spacing={2} className={classes.courseHeader}>
		<Grid item>
		<Typography variant="body1">
		{courseLabels.courseCapLabel}
		</Typography>
		</Grid>
		<Grid item>
		{_.includes(['name', 'start_date', 'end_date'], sort) ? null :
		<Typography variant="body1" align="right">
		{sortOptions[sort]}
		</Typography>
		}
		</Grid>
		<Grid item>
		<Typography variant="body1" align="right">
		Dates
		</Typography>
		</Grid>
		</Grid>
		: null }
		</Paper>
	);
	
	const [helpOpen, setHelpOpen] = useState(false);
	
	const HelpButton = () => (
		<Button color="primary"
			variant='outlined'
			size='small'
			onClick={() => setHelpOpen(true)}
		>
			Help
		</Button>
	)
	
	const [settingsOpen, setSettingsOpen] = useState(false);
	
	const DisplaySettings = () => {
		const [exclusionsCopy, setExclusionsCopy] = useState(_.cloneDeep(exclusions));
		const [categoriesCopy, setCategoriesCopy] = useState(_.cloneDeep(showCategories));
		const [keywordCopy, setKeywordCopy] = useState(keyword); 
		const [sortCopy, setSortCopy] = useState(sort);
		const [groupCopy, setGroupCopy] = useState(group);
		const [timespanCopy, setTimespanCopy] = useState(timespan);
		const timespanDescription = _.has(timespanOptions, timespan) ? 
			timespan + dayjs(timespanOptions[timespan][0]).format(' (D MMM YYYY - ') + dayjs(timespanOptions[timespan][1]).format('D MMM YYYY)') : timespan;
		
		const selectAll = () => {
			setCategoriesCopy(_.mapValues(categoriesCopy, c => { return true; }))
		}
		
		const selectNone = () => {
			setCategoriesCopy(_.mapValues(categoriesCopy, c => { return false; }))
		}
		
		// Using this lengthy method to enforce order, otherwise Safari iOS makes its own order!
		const toggleOne = (key, checked) => {
			const toggled = _.chain({...categoriesCopy, [key]: checked})
				.toPairs().sortBy(0).fromPairs().value();
			setCategoriesCopy(toggled);
		}
		
		return (
		<>
		<Typography >
		{timespanDescription}
		<IconButton aria-label="settings" color="primary" size='small' 
				onClick={() => {setSettingsOpen(true)}}
			>
		<EditOutlinedIcon />
		</IconButton>
		</Typography>

		<Dialog
		open={settingsOpen}
		onClose={() => setSettingsOpen(false)}
		fullScreen={!isSm}
		aria-labelledby="setings-dialog-title"
		className={classes.settingsDialog}
		>
		<DialogTitle id="settting-dialog-title">{courseLabels.courseCapLabel} Display Settings</DialogTitle>
		<DialogContent dividers>
		<Grid container spacing={3}>
		<Grid
		  container
		  spacing={3}
		  item xs={12} sm={6}
		  direction="column"
		  justifyContent="flex-start"
		  alignItems="flex-start"
		>
		<Grid item>
		<FormControl className={classes.settingsSelect} >
		<FormLabel>Show Timespan</FormLabel>
		<Select 
			size="small"
			value={timespanCopy} 
			onChange={(event) => setTimespanCopy(event.target.value)} 
		>
		{_.map(timespanOptions, (t, i) => (
			<MenuItem key={i} value={i}>{i}</MenuItem>
		))}
		</Select>
		</FormControl>
		</Grid>
		<Grid item>
		<FormControl component="fieldset">
		<FormLabel >Exclude</FormLabel>
		<FormGroup className={classes.boxAlignTop}>
		{_.map(exclusionsCopy, (e, i) => (
			<FormControlLabel 
				key={i}
				control={<Checkbox 
					checked={e} 
					onChange={(event) => setExclusionsCopy({...exclusionsCopy, [i]: event.target.checked})} 
				/>}
				label={_.capitalize(i + ' ' + courseLabels.coursesLabel)}
			/>
		))}
		</FormGroup>
		</FormControl>
		</Grid>
		<Grid item>
		<FormControl className={classes.settingsSelect} >
		<FormLabel>Sort By...</FormLabel>
		<Select size="small" value={groupCopy} 
			onChange={(event) => setGroupCopy(event.target.value)} 
		>
		{_.map(groupOptions, (g, i) => (
			<MenuItem key={i} value={i}>{g}</MenuItem>
		))}
		</Select>
		</FormControl>
		</Grid>
		<Grid item>
		<FormControl className={classes.settingsSelect} >
		<FormLabel>... Then By</FormLabel>
		<Select size="small" value={sortCopy} 
			onChange={(event) => setSortCopy(event.target.value)}
		>
		{_.map(sortOptions, (s, i) => (
			<MenuItem key={i} value={i}>{s}</MenuItem>
		))}
		</Select>
		</FormControl>
		</Grid>
		</Grid>
		<Grid container
		  spacing={3}
		  item xs={12} sm={6}
		  direction="column"
		  justifyContent="flex-start"
		  alignItems="flex-start"
		>
		<Grid item>
		<FormControl className={classes.settingsTextField}>
		<FormLabel>Search For Keyword</FormLabel>
		
		<TextField
				variant="outlined"
				size="small"
				fullWidth
				error={keywordCopy.length > 0 && keywordCopy.length < 3}
				helperText={keywordCopy.length > 0 && keywordCopy.length < 3 ? 'Enter at least three characters to use keyword search' : null}
				value={keywordCopy}
				onChange={(event) => setKeywordCopy(event.target.value)}
			/>
		
		</FormControl>
		</Grid>
		<Grid item>
		<FormControl component="fieldset">
		<FormLabel >Show {_.capitalize(Pluralize(courseLabels.category))}</FormLabel>
		<div className={classes.formLeftButtons}>
		<Button variant='outlined' color='primary' size='small' onClick={(e) => selectAll()}>Tick All</Button>
		<Button variant='outlined' color='primary' size='small' onClick={(e) => selectNone()}>Untick All</Button>
		</div>
		<FormGroup className={classes.boxAlignTop}>
		{_.map(categoriesCopy, (c, i) => (
			<FormControlLabel 
				key={i}
				control={<Checkbox 
					checked={c} 
					onChange={(event) => toggleOne(i, event.target.checked)} 
				/>}
				label={courseCategories[i] ? maybeWithPrefix(i, _.capitalize(courseCategories[i].description), true) : 'Other / uncategorised'}
			/>
		))}
		</FormGroup>
		</FormControl>
		</Grid>
		</Grid>
        </Grid>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setSettingsOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => {

			setExclusions(exclusionsCopy); 
			
			let k = keywordCopy.trim().toUpperCase();
			if(k.length === 0 || k.length > 2){
				setKeyword(k);
			} else {
				setKeyword('');
			}

			setShowCategories(categoriesCopy);
			setSort(sortCopy);
			setGroup(groupCopy);
			setTimespan(timespanCopy);
			setSettingsOpen(false);
			
			}} color="primary" autoFocus>
            Apply Changes
          </Button>
        </DialogActions>
		</Dialog>
		</>
	)}
	
	const scrollToRef = (ref) => window.scrollTo(0, ref.current.offsetTop - 64);
	const anchorTop = useRef(null)
	const backToTop = () => scrollToRef(anchorTop);
	
	const PaginatorMemo = ({setPage}) => (<Paginator
		itemsLabel={courseLabels.coursesCapLabel}
		noneFound={fetchedCourses > 0 && filteredCourses.length === 0}
		noneFoundMessage={'No '+courseLabels.coursesLabel+' found. Try viewing a different timespan, or widening your search criteria.'}
		setSettingsOpen={setSettingsOpen}
		settingsLabel='Filter'
		itemsLength={filteredCourses.length}
		isSmd={isSmd}
		isMd={isMd}
		isMdl={isMdl} 
		page={page}
		pages={pages} 
		perPage={perPage} 
		setPage={setPage}
	/>)
	
	return (
	<>
	<div ref={anchorTop}></div>
	<ErrorDisplay />
	<PageTitle title={courseLabels.coursesCapLabel} other={<HelpButton/>} withoutPaper />
	<CoursesHelp
		helpOpen={helpOpen}
		handleChange={setHelpOpen}
		isSm={isSm} 
		coursesCapLabel={courseLabels.coursesCapLabel}
		courseCapLabel={courseLabels.courseCapLabel}		
		coursesLabel={courseLabels.coursesLabel} 
		courseLabel={courseLabels.courseLabel}
		category={courseLabels.category} 
		member_no={user.member_no} 
		general_enrol_on={enrolmentsSettings.general_enrol_on} 
		early_enrol_on={enrolmentsSettings.early_enrol_on} 
		userCanEnrol={permissions.user_can_enrol}
	/>
	{(fetchedCourses === 0 && !errorMsg) || fetchingCourses ? <Waiting 
		message={'Fetching '+courseLabels.coursesLabel+'. Please wait...'} 
		withPaper 
	/> :
	<>
	<DisplaySettings/>
	<PaginatorMemo setPage={setPage} />
	{
		_.map(showingCourses, (sh, i) => (
        <div key={i}>
		<CourseGroupHeader g={i}/>
		{sh.map((course,i) =>
		<CoursePanel key={i}
			course={course} 
			sortHeader={_.includes(['name', 'start_date', 'end_date'], sort) ? 
				null : ('is_new' === sort ? 
					(course.is_new ? 'New' : 'Not new') :
					(_.includes(['start_time', 'code', 'day', 'tutor'], sort) ? 
						getDescription(sort, course[sort]) :
						maybeWithPrefix(course[sort], getDescription(sort, course[sort]))
					)
			)}
			fieldNames={fieldNames}
			courseCapLabel={courseLabels.courseCapLabel}
			courseLabel={courseLabels.courseLabel}
			categoryLabel={courseLabels.category} 
			frequency={courseFrequencies[course.frequency_code]}
			type={courseTypes[course.type_code]}
			tutor={courseTutors[course.tutor]}
			tutor2={courseTutors[course.tutor2]}
			venue={courseVenues[course.venue_code]}
			displayTutor={fields.tutor.displayable}
			displayTutor2={fields.tutor2.displayable}
			displayAdditionalDetail={fields.additional_detail.displayable}
			userCanEnrol={permissions.user_can_enrol}
			enrolmentCounts={enrolmentCounts}
			unVaxxed={unVaxxed && isTrue(course.vaccination_required)}
			fullVaxLabel={memberLabels.fully_vaccinated}
		/>
		)}
		</div>
		))
	}
	{ filteredCourses.length === 0 ? null :
	<PaginatorMemo setPage={(p) => {setPage(p); backToTop();}} />
	}
	</>
	}
	</>
)};

export default Courses